import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronUpIcon } from '@/components/icons';
import { ShoppingCart } from 'lucide-react';
import { calculateSubtotal, calculatePlatformFees, calculateTotalPrice } from '@/features/cart/utils/cartUtils';
import { formatCurrencyForDisplay } from '@/lib/utils';
import type { CartItemResponse, CartResponse } from '@/features/cart/types';

interface PriceBreakdown {
  totalPrice: number;
  perGuestBreakdown: Array<{
    addOns: Array<{
      addOn: {
        add_on: {
          name: string;
        };
      };
      price: number;
    }>;
  }>;
}

interface FloatingPriceSummaryProps {
  priceBreakdown: PriceBreakdown | null;
  guestCount: number;
  eventBookingCategory: {
    booking_category: {
      name: string;
      business?: {
        handle: string;
      } | null;
    };
    price: number;
  };
  onAddToCart: () => void;
  addingToCart: boolean;
  buttonText?: string;
  isUpdateMode?: boolean;
  onUpdateCart?: () => void;
  cartId?: string | null;
  addOnCount?: number;
  isLoadingCarts?: boolean;
  hasCartChanges?: boolean;
  hasViewCartButton?: boolean;
  hasCartItemForEBC?: boolean;
  // Cart mode props
  isCartMode?: boolean;
  onDownloadInvoice?: () => void;
  isDownloadingInvoice?: boolean;
  cartItems?: CartItemResponse[];
  cart?: CartResponse | null;
  // Table booking props
  isTableBooking?: boolean;
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;

  /** All selected table slots for table bookings */
  tableSlots?: Array<{
    tableNumber: string;
    tableSize: number;
    guestCount: number;
    basePrice: number;
  }>;
  /** The item type string (e.g. "Table", "Chair", "Bed") */
  itemType?: string;
}

const FloatingPriceSummary: React.FC<FloatingPriceSummaryProps> = ({
  priceBreakdown,
  guestCount,
  eventBookingCategory,
  onAddToCart,
  addingToCart,
  buttonText = 'Add to Cart',
  isUpdateMode = false,
  onUpdateCart,
  addOnCount = 0,
  isLoadingCarts = false,
  hasCartChanges = false,
  hasViewCartButton = false,
  hasCartItemForEBC = false,
  isCartMode = false,
  onDownloadInvoice,
  isDownloadingInvoice = false,
  cartItems = [],
  isTableBooking = false,

  tableSlots = [],
  currency,
  itemType,
}) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  // Calculate cart totals when in cart mode
  const subtotal = isCartMode ? calculateSubtotal(cartItems) : 0;
  const platformFees = isCartMode ? calculatePlatformFees(cartItems) : 0;
  const totalPrice = isCartMode ? calculateTotalPrice(cartItems) : (priceBreakdown?.totalPrice || 0);

  // Shift up when in update mode and View Cart button is visible
  const bottomPosition = isUpdateMode && hasViewCartButton 
    ? 'bottom-[calc(2rem+3.75rem)]' 
    : 'bottom-8';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 25, delay: 0.1 }}
      className={`fixed ${bottomPosition} right-3 md:right-4 lg:right-6 xl:right-10 2xl:right-[6vw] z-50`}
    >
      <div className="relative w-80">
        {/* Main Price Display */}
        <motion.div
          layout
          className="bg-black/80 backdrop-blur-md border border-gray-600/50 rounded-2xl shadow-2xl overflow-hidden w-full"
        >
          {/* Price Header - Always Visible */}
          <motion.div
            layout
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            className="px-6 py-4 flex items-center space-x-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
          >
            <div className="text-right flex-1">
              <div className="text-2xl font-bold text-white">
                {formatPrice(totalPrice)}
              </div>
              <div className="text-sm text-gray-300">
                {isCartMode ? 'Order Summary' : (
                  <>
                    {guestCount} guest{guestCount !== 1 ? 's' : ''}
                    {addOnCount > 0 && (
                      <span className="ml-2">• {addOnCount} addon{addOnCount !== 1 ? 's' : ''}</span>
                    )}
                  </>
                )}
              </div>
            </div>
            <motion.div
              animate={{ rotate: isSummaryExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="w-8 h-8 rounded-full bg-gray-700/80 hover:bg-gray-600 flex items-center justify-center transition-all duration-200"
            >
              <ChevronUpIcon />
            </motion.div>
          </motion.div>

          {/* Expandable Summary Details */}
          <AnimatePresence>
            {isSummaryExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="border-t border-gray-700/50"
              >
                <div className="px-6 py-4 space-y-4 max-h-[50vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                  {isCartMode ? (
                    /* Order Summary for Cart Mode */
                    <div>
                      <h3 className="text-lg font-bold text-white mb-3">Order Summary</h3>
                      
                      {/* Subtotal */}
                      <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                        <span className="text-gray-300">Subtotal</span>
                        <span className="text-white font-medium">
                          {formatPrice(subtotal)}
                        </span>
                      </div>

                      {/* Platform Fees */}
                      <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                        <span className="text-gray-300">Platform Fees</span>
                        <span className="text-white font-medium">
                          {formatPrice(platformFees)}
                        </span>
                      </div>

                      {/* Total */}
                      <div className="flex justify-between items-center py-3 border-t border-gray-600/50">
                        <span className="text-lg font-bold text-white">Total</span>
                        <span className="text-2xl font-bold text-blue-400">
                          {formatPrice(totalPrice)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    /* Booking Details for Booking Mode */
                    <div>
                      <h3 className="text-lg font-bold text-white mb-3">Booking Summary</h3>
                      
                      {/* Guest Count - always at the top */}
                      <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                        <span className="text-gray-300">Total Guests</span>
                        <span className="text-white font-medium">{guestCount}</span>
                      </div>

                      {/* Table Info (for table bookings) - Show all selected tables */}
                      {isTableBooking && tableSlots && tableSlots.filter(slot => slot.guestCount > 0).length > 0 && (
                        <>
                          {/* Per-seat price info */}
                          <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                            <span className="text-gray-300">Price per seat</span>
                            <span className="text-white font-medium">
                              {formatPrice(eventBookingCategory?.price || 0)}
                            </span>
                          </div>
                          
                          {/* Table header row */}
                          <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-700/50">
                            <span className="text-gray-400 text-sm">{itemType || 'Table'}</span>
                            <span className="text-gray-400 text-sm text-center">Size</span>
                            <span className="text-gray-400 text-sm text-right">Price</span>
                          </div>
                          
                          {/* Each selected table */}
                          {tableSlots.filter(slot => slot.guestCount > 0).map((slot, index) => (
                            <div key={`${slot.tableNumber}-${index}`} className="grid grid-cols-3 gap-2 py-2 border-b border-gray-700/50">
                              <span className="text-white font-medium">{slot.tableNumber}</span>
                              <span className="text-white font-medium text-center">{slot.tableSize}</span>
                              <span className="text-white font-medium text-right">{formatPrice(slot.basePrice)}</span>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Base Price - only show for non-table bookings */}
                      {!isTableBooking && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                          <span className="text-gray-300">
                            {eventBookingCategory?.booking_category?.name}
                          </span>
                          <span className="text-white font-medium">
                            {formatPrice(eventBookingCategory?.price || 0)} × {guestCount}
                          </span>
                        </div>
                      )}

                      {/* Add-ons */}
                      {priceBreakdown?.perGuestBreakdown?.map((guest, guestIndex) => {
                        // Aggregate add-ons by name with quantities
                        const aggregatedAddOns = guest.addOns.reduce((acc, addOn) => {
                          const name = addOn.addOn.add_on.name;
                          if (!acc[name]) {
                            acc[name] = { count: 0, unitPrice: addOn.price, totalPrice: 0 };
                          }
                          acc[name].count++;
                          acc[name].totalPrice += addOn.price;
                          return acc;
                        }, {} as Record<string, { count: number; unitPrice: number; totalPrice: number }>);
                        
                        const addOnEntries = Object.entries(aggregatedAddOns);
                        
                        return addOnEntries.length > 0 && (
                          <div key={guestIndex} className="py-2 border-b border-gray-700/50">
                            <div className="text-sm text-gray-400 mb-1">Guest {guestIndex + 1} Add-ons</div>
                            {addOnEntries.map(([name, { count, totalPrice }], addOnIndex) => (
                              <div key={addOnIndex} className="flex justify-between items-center text-sm">
                                <span className="text-gray-300">
                                  {name}{count > 1 && <span className="text-blue-400 ml-1">×{count}</span>}
                                </span>
                                <span className="text-white">{formatPrice(totalPrice)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}

                      {/* Total */}
                      <div className="flex justify-between items-center py-3 border-t border-gray-600/50">
                        <span className="text-lg font-bold text-white">Total</span>
                        <span className="text-2xl font-bold text-blue-400">
                          {formatPrice(priceBreakdown?.totalPrice || 0)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Add to Cart / Update Cart / Download Invoice Button */}
        {isCartMode && onDownloadInvoice ? (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 25, delay: 0.2 }}
            onClick={onDownloadInvoice}
            disabled={isDownloadingInvoice}
            className={`mt-4 w-full px-6 py-3 rounded-xl shadow-2xl flex items-center justify-center space-x-2 transition-all duration-200 border ${
              isDownloadingInvoice
                ? 'bg-gray-600 hover:bg-gray-600 text-gray-400 border-gray-500/50 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white border-green-500/50'
            }`}
          >
            {isDownloadingInvoice ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="font-semibold">Downloading...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-semibold">Download Invoice</span>
              </>
            )}
          </motion.button>
        ) : (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 25, delay: 0.2 }}
            onClick={isUpdateMode && onUpdateCart ? onUpdateCart : onAddToCart}
            disabled={addingToCart || isLoadingCarts || (isUpdateMode && !hasCartChanges && !(hasCartItemForEBC && guestCount === 0))}
            className={`mt-4 w-full px-6 py-3 rounded-xl shadow-2xl flex items-center justify-center space-x-2 transition-all duration-200 border ${
              addingToCart || isLoadingCarts || (isUpdateMode && !hasCartChanges && !(hasCartItemForEBC && guestCount === 0))
                ? 'bg-gray-600 hover:bg-gray-600 text-gray-400 border-gray-500/50 cursor-not-allowed'
                : hasCartItemForEBC && guestCount === 0
                ? (isTableBooking 
                    ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-[0_0_20px_rgba(234,179,8,0.3)]'
                    : 'bg-red-800 hover:bg-red-900 text-white border-red-700/50')
                : isUpdateMode && hasCartChanges
                ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-[0_0_20px_rgba(234,179,8,0.3)]'
                : isUpdateMode && !hasCartChanges
                ? 'bg-blue-900 text-blue-300 border-blue-500/30'
                : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-500/50'
            }`}
          >
            {isLoadingCarts ? (
              <>
                <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin"></div>
                <span className="font-medium text-sm">Checking cart...</span>
              </>
            ) : addingToCart ? (
              <>
                <div className={`w-4 h-4 border-2 ${isUpdateMode && hasCartChanges ? 'border-black/30 border-t-black' : 'border-white/30 border-t-white'} rounded-full animate-spin`}></div>
                <span className="font-semibold">{isUpdateMode ? 'Updating...' : 'Adding...'}</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4" />
                <span className="font-semibold">{hasCartItemForEBC && guestCount === 0 ? (isTableBooking ? 'Update Cart' : 'Remove Item') : isUpdateMode && !hasCartChanges ? 'In Cart' : buttonText}</span>
              </>
            )}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default FloatingPriceSummary;
